import { onlineManager } from "@tanstack/react-query";
import { type ClassValue, clsx } from "clsx";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function hasValue(value: unknown): value is string | number {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed !== "" && trimmed !== "null";
}

export function joinPresent(parts: unknown[], separator = " ") {
  return parts.filter(hasValue).map(String).join(separator);
}

export function toYesNo(value: boolean) {
  return value ? "YES" : "NO";
}

export function calculateCommission(volume: number, pricePerLiter: number) {
  let commission_percentage: number;

  if (volume <= 30000) {
    commission_percentage = 4.0;
  } else if (volume > 30000 && volume <= 250000) {
    commission_percentage = 3.5;
  } else if (volume > 250000 && volume <= 500000) {
    commission_percentage = 3.0;
  } else if (volume > 500000 && volume <= 1000000) {
    commission_percentage = 2.5;
  } else {
    commission_percentage = 2.0;
  }

  const total = volume * pricePerLiter;
  const commission = (commission_percentage / 100) * total;

  const formattedTotal = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ZAR",
  }).format(total);

  const formattedCommission = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ZAR",
  }).format(commission);

  const effectivePrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ZAR",
  }).format((total - commission) / volume || 0);

  const commissionPerLiter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ZAR",
  }).format(commission / volume || 0);

  return {
    total: formattedTotal,
    commission: formattedCommission,
    commission_percentage,
    effectivePrice,
    commissionPerLiter,
  };
}

export function formatToTwitterDate(dateTimeString: string) {
  const date = new Date(dateTimeString);

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();

  // Convert hours to AM/PM format
  const amPM = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert hours to 12-hour format

  const formattedDate = `${hours}:${
    minutes < 10 ? "0" : ""
  }${minutes} ${amPM} · ${month} ${day}, ${year}`;

  return formattedDate;
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(onlineManager.isOnline());

  useEffect(() => {
    return onlineManager.subscribe(setIsOnline);
  }, []);

  return isOnline;
}

export function sanitizeNumber(number: string) {
  //Number comes in as 083 554 3133
  //Needs to go out as +27835543133

  const sanitizedNumber = number.replace(/\s/g, "");

  //Check if number starts with 0
  if (sanitizedNumber.startsWith("0")) {
    const sanitizedNumberWithCountryCode = `+27${sanitizedNumber.slice(1)}`;
    return sanitizedNumberWithCountryCode;
  } else {
    return sanitizedNumber;
  }
}

export function formatDealStatus(status: string) {
  if (!status) {
    return "Unknown";
  }

  //Make uppercase and split with _
  const formattedStatus = status.toUpperCase().replace(/_/g, " ");
  return formattedStatus;
}

/**
 * Generates a slug from a file key.
 * @param {string} fileKey - The file key to generate a slug from.
 * @returns {string} The generated slug.
 */
export function generateSlug(fileKey: string): string {
  return fileKey
    .split(".")[0]
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function checkEnv(env: NodeJS.ProcessEnv) {
  const required = [
    "DOMAIN_URL",
    "BETTER_AUTH_SECRET",
    "BETTER_AUTH_URL",
    "DATABASE_URL",
    "NODE_ENV",
    "DISCORD_WEBHOOK_URL",
    "DISCORD_WEBHOOK_URL_ERROR",
    "GOTENBERG_URL",
    "GOTENBERG_USER",
    "GOTENBERG_PASS",
    "R2_ACCESS_KEY_ID",
    "R2_DEV_URL",
    "R2_PUBLIC_URL",
    "R2_SECRET_ACCESS_KEY",
    "REDIS_PASSWORD",
    "REDIS_URL",
    "REPLICA_URL",
    "TWILIO_ACCOUNT_SID",
    "TWILIO_AUTH_TOKEN",
    "TWILIO_MESSAGING_SERVICE_SID",
    "TWILIO_PHONE_NUMBER",
    "SENDKIT_PUBLIC_KEY",
    "SENDKIT_PRIVATE_KEY",
  ];

  const missing = required.filter((key) => !env[key]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(", ")}`,
    );
  } else {
    console.log("All required environment variables are set");
  }
}
